import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTestRuns, submitEvaluation, getEvaluationTags, createEvaluationTag, triggerAutoEvaluation, applyManualOverride, getPrompt } from '../services/api';
import SuggestionModal from '../components/SuggestionModal';
import { useFeedback } from '../contexts/FeedbackContext';

const TestEvaluation = () => {
  const { projectId, testSetId, promptVersionId } = useParams();
  const navigate = useNavigate();
  const { showContextualFeedback } = useFeedback();
  
  const [testRuns, setTestRuns] = useState([]);
  const [evaluationTags, setEvaluationTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTagModal, setShowTagModal] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', color: '#3B82F6' });
  const [currentEvaluation, setCurrentEvaluation] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set()); // Track which cards are expanded
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [selectedTestRun, setSelectedTestRun] = useState(null);
  const [promptData, setPromptData] = useState(null);

  useEffect(() => {
    loadData();
  }, [testSetId, promptVersionId]);

  // Set up polling for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      // Only poll if there are pending evaluations
      const hasPendingEvaluations = testRuns.some(run => run.auto_eval_status === 'pending');
      if (hasPendingEvaluations) {
        loadDataSilently();
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, [testRuns, testSetId, promptVersionId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // For demo purposes, we'll use a hardcoded organization ID
      // In a real app, this would come from the current user's organization
      const organizationId = 'a5ac7a71-8cf6-4cf2-a106-98a66a5a2d38';
      
      const [testRunsData, tagsData] = await Promise.all([
        getTestRuns(testSetId, promptVersionId),
        getEvaluationTags(organizationId)
      ]);
      
      setTestRuns(testRunsData);
      setEvaluationTags(tagsData);

      // Get prompt data from the first test run if available
      if (testRunsData.length > 0 && testRunsData[0].prompt_id) {
        const promptResult = await getPrompt(testRunsData[0].prompt_id);
        setPromptData(promptResult);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Silent data loading for real-time updates (no loading state)
  const loadDataSilently = useCallback(async () => {
    try {
      const organizationId = 'a5ac7a71-8cf6-4cf2-a106-98a66a5a2d38';
      const testRunsData = await getTestRuns(testSetId, promptVersionId);
      setTestRuns(testRunsData);
    } catch (error) {
      console.error('Error loading data silently:', error);
    }
  }, [testSetId, promptVersionId]);

  const handleEvaluationSubmit = async (runId, score, comment, tagIds) => {
    try {
      await submitEvaluation(runId, { score, comment, tagIds });
      loadData(); // Refresh to show updated evaluations

      
      showContextualFeedback({
        actionName: 'Submit Evaluation',
        actionContext: `Submitted evaluation with score ${score}/5`,
        position: 'bottom-right'
      });
    } catch (error) {
      console.error('Error submitting evaluation:', error);
    }
  };

  const handleManualOverride = async (runId, newStatus) => {
    const reason = prompt(`Why are you overriding this result to ${newStatus.toUpperCase()}?`);
    if (reason) {
      try {
        await applyManualOverride(runId, newStatus, reason);
        loadDataSilently(); // Refresh to show updated results
      } catch (error) {
        console.error('Error applying manual override:', error);
      }
    }
  };

  const handleRetriggerEvaluation = async (runId) => {
    try {
      await triggerAutoEvaluation(runId);
      loadDataSilently(); // Refresh to show updated results

      // Show contextual feedback after retriggering evaluation
      showContextualFeedback({
        actionName: 'Retrigger Auto-Evaluation',
        actionContext: 'Retriggered automatic evaluation for test result',
        position: 'bottom-right'
      });
    } catch (error) {
      console.error('Error retriggering evaluation:', error);
    }
  };

  const toggleCardExpansion = (runId) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(runId)) {
      newExpanded.delete(runId);
    } else {
      newExpanded.add(runId);
    }
    setExpandedCards(newExpanded);
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    try {
      await createEvaluationTag({
        ...newTag,
        organizationId: 'a5ac7a71-8cf6-4cf2-a106-98a66a5a2d38' // In real app, get from user context
      });
      setNewTag({ name: '', color: '#3B82F6' });
      setShowTagModal(false);
      loadData();
    } catch (error) {
      console.error('Error creating tag:', error);
    }
  };

  const handleSuggestImprovements = (testRun) => {
    setSelectedTestRun(testRun);
    setShowSuggestionModal(true);
  };

  const handleSuggestionAccepted = (suggestion) => {
    loadData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading test results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/projects/${projectId}/test-sets/${testSetId}`)}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back to Test Set
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Test Evaluation
                </h1>
                <p className="text-sm text-gray-500">
                  {testRuns.length} test results
                </p>
              </div>
            </div>
            
            <button
              onClick={() => setShowTagModal(true)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Manage Tags
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {testRuns.map((run, index) => (
            <TestRunEvaluationCard
              key={run.id}
              run={run}
              index={index}
              evaluationTags={evaluationTags}
              onEvaluationSubmit={handleEvaluationSubmit}
              onManualOverride={handleManualOverride}
              onRetriggerEvaluation={handleRetriggerEvaluation}
              onSuggestImprovements={handleSuggestImprovements}
              isExpanded={expandedCards.has(run.id)}
              onToggleExpansion={() => toggleCardExpansion(run.id)}
            />
          ))}
        </div>
      </div>

      {/* Tag Management Modal */}
      {showTagModal && (
        <TagManagementModal
          evaluationTags={evaluationTags}
          newTag={newTag}
          setNewTag={setNewTag}
          onCreateTag={handleCreateTag}
          onClose={() => setShowTagModal(false)}
        />
      )}

      {/* Suggestion Modal */}
      {showSuggestionModal && selectedTestRun && promptData && (
        <SuggestionModal
          isOpen={showSuggestionModal}
          onClose={() => setShowSuggestionModal(false)}
          promptId={selectedTestRun.prompt_id}
          testRunId={selectedTestRun.id}
          originalPrompt={promptData.versions?.find(v => v.id === promptVersionId)?.content || ''}
          onSuggestionAccepted={handleSuggestionAccepted}
        />
      )}
    </div>
  );
};

// Component for individual test run evaluation
const TestRunEvaluationCard = ({ run, index, evaluationTags, onEvaluationSubmit, onManualOverride, onRetriggerEvaluation, onSuggestImprovements, isExpanded, onToggleExpansion }) => {
  const [feedback, setFeedback] = useState({
    score: run.feedback_score || null,
    comment: run.feedback_comment || '',
    selectedTags: run.assigned_tags?.map(tag => tag.id) || []
  });
  const [showDetails, setShowDetails] = useState(false);

  const handleScoreChange = (score) => {
    const newFeedback = { ...feedback, score };
    setFeedback(newFeedback);
    
    // Auto-submit score changes
    onEvaluationSubmit(run.id, score, newFeedback.comment, newFeedback.selectedTags);
  };

  const handleTagToggle = (tagId) => {
    const newSelectedTags = feedback.selectedTags.includes(tagId)
      ? feedback.selectedTags.filter(id => id !== tagId)
      : [...feedback.selectedTags, tagId];
    
    const newFeedback = { ...feedback, selectedTags: newSelectedTags };
    setFeedback(newFeedback);
    
    // Auto-submit tag changes
    onEvaluationSubmit(run.id, newFeedback.score, newFeedback.comment, newSelectedTags);
  };

  const handleCommentSubmit = () => {
    onEvaluationSubmit(run.id, feedback.score, feedback.comment, feedback.selectedTags);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={onToggleExpansion}
              className="flex items-center space-x-2 text-lg font-medium text-gray-900 hover:text-gray-700"
            >
              <svg 
                className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Test Case #{index + 1}
            </button>
            {/* Auto-evaluation status badge */}
            {run.auto_eval_status && (
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  run.auto_eval_status === 'pass' 
                    ? 'badge-status-success'
                    : run.auto_eval_status === 'pending' 
                    ? 'badge-status-warning'
                    : run.auto_eval_status === 'fail'
                    ? 'badge-status-error'
                    : 'badge-status-info'
                }`}>
                  {run.auto_eval_status === 'pass' ? 'Pass' : run.auto_eval_status === 'pending' ? 'Pending' : run.auto_eval_status === 'fail' ? 'Fail' : 'Pending'}
                </span>
                {run.manual_override && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Manual Override
                  </span>
                )}
              </div>
            )}
          </div>
          {isExpanded && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </button>
          )}
        </div>

        {/* Collapsible Content */}
        {isExpanded && (
          <>
            {/* Test Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Input
              </label>
              <div className="bg-gray-50 rounded-md p-3 border">
                <pre className="whitespace-pre-wrap text-sm text-gray-900">
                  {run.input_text}
                </pre>
              </div>
            </div>

        {/* Generated Output */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Generated Output
            </label>
            {/* Execution Metadata */}
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              {run.model_name && (
                <span className="bg-gray-100 px-2 py-1 rounded">
                  {run.model_name}
                </span>
              )}
              {run.execution_time_ms && (
                <span>
                  {run.execution_time_ms}ms
                </span>
              )}
              {run.tokens_used && (
                <span>
                  {run.tokens_used} tokens
                </span>
              )}
              <span>
                {new Date(run.created_at).toLocaleString()}
              </span>
            </div>
          </div>
          <div className="bg-blue-50 rounded-md p-4 border border-blue-200">
            <pre className="whitespace-pre-wrap text-sm text-gray-900">
              {run.output_text}
            </pre>
          </div>
        </div>

        {/* Expected Behavior (if available) */}
        {showDetails && run.expected_behavior && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expected Behavior
            </label>
            <div className="bg-gray-50 rounded-md p-3 border">
              <pre className="whitespace-pre-wrap text-sm text-gray-600">
                {run.expected_behavior}
              </pre>
            </div>
          </div>
        )}

        {/* Auto-Evaluation Results */}
        {run.auto_eval_score !== null && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Auto-Evaluation Results
              </label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  Score: {(run.auto_eval_score * 100).toFixed(0)}%
                </span>
                {run.auto_eval_model && (
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {run.auto_eval_model}
                  </span>
                )}
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-celadon-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    Passed
                  </p>
                  <p className="text-sm text-gray-500">This test case meets all evaluation criteria</p>
                </div>
              </div>
            </div>
            
            {/* Auto-evaluation actions */}
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onManualOverride(run.id, run.auto_eval_status === 'pass' ? 'fail' : 'pass')}
                  className="text-sm text-celadon-600 hover:text-celadon-800"
                >
                  Override to {run.auto_eval_status === 'pass' ? 'Fail' : 'Pass'}
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => onRetriggerEvaluation(run.id)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Re-evaluate
                </button>
                {/* Show suggestion button for failed tests */}
                {run.auto_eval_status === 'fail' && (
                  <>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => onSuggestImprovements(run)}
                      className="text-sm text-celadon-600 hover:text-celadon-800 font-medium"
                    >
                      <RobotIcon />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pending evaluation notice */}
        {run.auto_eval_status === 'pending' && (
          <div className="mb-6">
            <div className="bg-warning-50 border border-warning-200 rounded-md p-4">
              <div className="flex">
                <svg className="w-5 h-5 text-warning-600 mr-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <div>
                  <p className="text-sm font-medium text-warning-800">Auto-evaluation pending</p>
                  <p className="text-sm text-warning-700">This test case is queued for automatic evaluation. Results will appear shortly.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Evaluation Section */}
        <div className="border-t pt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Evaluation</h4>
          
          {/* Score Buttons */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rating
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => handleScoreChange(1)}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  feedback.score === 1
                    ? 'bg-green-100 border-green-500 text-green-700'
                    : 'bg-gray-50 border-gray-300 text-gray-500 hover:border-green-300'
                }`}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                onClick={() => handleScoreChange(0)}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  feedback.score === 0
                    ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                    : 'bg-gray-50 border-gray-300 text-gray-500 hover:border-yellow-300'
                }`}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                onClick={() => handleScoreChange(-1)}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  feedback.score === -1
                    ? 'bg-red-100 border-red-500 text-red-700'
                    : 'bg-gray-50 border-gray-300 text-gray-500 hover:border-red-300'
                }`}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tags */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {evaluationTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagToggle(tag.id)}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    feedback.selectedTags.includes(tag.id)
                      ? 'text-white'
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  }`}
                  style={{
                    backgroundColor: feedback.selectedTags.includes(tag.id) ? tag.color : undefined,
                    borderColor: tag.color,
                    borderWidth: '1px'
                  }}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comment (optional)
            </label>
            <div className="flex space-x-2">
              <textarea
                value={feedback.comment}
                onChange={(e) => setFeedback({ ...feedback, comment: e.target.value })}
                placeholder="Add specific feedback about this output..."
                rows={2}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleCommentSubmit}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 self-start"
              >
                Save
              </button>
            </div>
          </div>

          {/* Suggest Improvements for Poor Ratings */}
          {(feedback.score === -1 || run.auto_eval_status === 'failed') && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    Need help improving this prompt?
                  </p>
                  <p className="text-sm text-gray-600">
                    Let AI analyze the failure and suggest improvements
                  </p>
                </div>
                <button
                  onClick={() => onSuggestImprovements(run)}
                  className="bg-celadon-600 text-white px-4 py-2 rounded-md hover:bg-celadon-700 text-sm font-medium flex items-center space-x-2"
                >
                  <RobotIcon />
                  <span>Get AI Suggestions</span>
                </button>
              </div>
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

// Tag Management Modal Component
const TagManagementModal = ({ evaluationTags, newTag, setNewTag, onCreateTag, onClose }) => {
  const colorOptions = [
    '#7eae8a', '#9cc4a5', '#b4d2ba', '#c3dec8',
    '#737373', '#525252', '#404040', '#262626'
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl bg-white rounded-lg shadow-lg">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Manage Evaluation Tags
          </h3>
          
          {/* Existing Tags */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-700 mb-3">Current Tags</h4>
            <div className="flex flex-wrap gap-2">
              {evaluationTags.map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: tag.color }}
                >
                  {tag.name}
                </span>
              ))}
              {evaluationTags.length === 0 && (
                <p className="text-gray-500 text-sm">No tags created yet.</p>
              )}
            </div>
          </div>
          
          {/* Create New Tag */}
          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-700 mb-3">Create New Tag</h4>
            <form onSubmit={onCreateTag} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tag Name *
                </label>
                <input
                  type="text"
                  value={newTag.name}
                  onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                  placeholder="e.g., Accurate, Creative, Needs Work"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-celadon-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div className="flex space-x-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewTag({ ...newTag, color })}
                      className={`w-8 h-8 rounded-full border-2 ${
                        newTag.color === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-celadon-600 text-white rounded-md hover:bg-celadon-700"
                >
                  Create Tag
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple SVG icons to replace emojis
const LightBulbIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const RobotIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Component to show detailed auto-evaluation criteria
const AutoEvaluationDetails = ({ criteria }) => {
  try {
    const parsedCriteria = typeof criteria === 'string' ? JSON.parse(criteria) : criteria;
    
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900">Detailed Evaluation:</h4>
        
        {/* Individual criteria scores */}
        {parsedCriteria.scores && Object.keys(parsedCriteria.scores).length > 0 && (
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wider">Criteria Scores:</h5>
            {Object.entries(parsedCriteria.scores).map(([criterion, score]) => (
              <div key={criterion} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 capitalize">{criterion.replace('_', ' ')}</span>
                <span className="font-medium">{(score * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        )}
        
        {/* Strengths */}
        {parsedCriteria.strengths && parsedCriteria.strengths.length > 0 && (
          <div>
            <h5 className="text-xs font-medium text-celadon-700 uppercase tracking-wider mb-1">Strengths:</h5>
            <ul className="text-sm text-gray-600 space-y-1">
              {parsedCriteria.strengths.map((strength, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-celadon-500 mr-2"><CheckIcon /></span>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Weaknesses */}
        {parsedCriteria.weaknesses && parsedCriteria.weaknesses.length > 0 && (
          <div>
            <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">Areas for Improvement:</h5>
            <ul className="text-sm text-gray-600 space-y-1">
              {parsedCriteria.weaknesses.map((weakness, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-gray-500 mr-2"><XIcon /></span>
                  {weakness}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Suggestions */}
        {parsedCriteria.suggestions && parsedCriteria.suggestions.length > 0 && (
          <div>
            <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">Suggestions:</h5>
            <ul className="text-sm text-gray-600 space-y-1">
              {parsedCriteria.suggestions.map((suggestion, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-gray-600 mr-2"><LightBulbIcon /></span>
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  } catch (error) {
    return (
      <div className="text-sm text-gray-500">
        Unable to display detailed evaluation criteria.
      </div>
    );
  }
};

export default TestEvaluation; 